import React, { forwardRef, useImperativeHandle, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { cn } from "@/lib/utils";
import {
    Card,
    CardContent,
} from "@/components/ui/Card"
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/Table"
import DeleteIcon from "@/components/icons/DeleteIcon";
import {
    calculateControlEfficacy,
    CONTROL_EFFICACY_MAX_VALUE,
    CONTROL_EFFICACY_MIN_VALUE,
    getControlEfficacyColor,
} from "@/services/controlEfficacyService";

const generateControlRowId = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID()
    }
    return `control-row-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

const createEmptyControlRow = (overrides = {}) => ({
    id: overrides.id ?? generateControlRowId(),
    persistedId: overrides.persistedId ?? null,
    risk: overrides.risk ?? "",
    category: overrides.category ?? "",
    design: overrides.design ?? "",
    implementation: overrides.implementation ?? "",
    controlEfficacy: overrides.controlEfficacy ?? "",
})

const ControlEfficacyTab = forwardRef(
    ({ rows, setRows, onControlEfficacyChange = () => { }, onRiskCategoryChange = () => { } }, ref) => {
        const controlRows = rows || [];
        const [enteringRowIds, setEnteringRowIds] = useState([]);
        const [removingRowIds, setRemovingRowIds] = useState([]);

        const handleControlRowChange = (rowId, field, value) => {
            let latestControlEfficacy = null
            let nextValue = value;
            if (field === 'design' || field === 'implementation') {
                if (nextValue.includes('.')) {
                    return;
                }
                if (nextValue === '') {
                    nextValue = '';
                } else {
                    let numeric = Number(nextValue);
                    if (Number.isNaN(numeric)) {
                        numeric = '';
                    } else if (numeric > CONTROL_EFFICACY_MAX_VALUE) {
                        numeric = CONTROL_EFFICACY_MAX_VALUE;
                    } else if (numeric < CONTROL_EFFICACY_MIN_VALUE) {
                        numeric = CONTROL_EFFICACY_MIN_VALUE;
                    }
                    nextValue = numeric === '' ? '' : numeric.toString();
                }
            }

            let updatedRowSnapshot = null;
            setRows((prev) =>
                prev.map((row) =>
                    row.id === rowId
                        ? (() => {
                            const updatedRow = { ...row, [field]: nextValue };
                            if (field === 'design' || field === 'implementation') {
                                const derived = calculateControlEfficacy(updatedRow.design, updatedRow.implementation);
                                updatedRow.controlEfficacy = derived !== null ? derived.toString() : '';
                                latestControlEfficacy = updatedRow.controlEfficacy;
                            }
                            updatedRowSnapshot = updatedRow;
                            return updatedRow;
                        })()
                        : row
                )
            )

            if (latestControlEfficacy !== null) {
                onControlEfficacyChange(rowId, latestControlEfficacy);
            }

            if (field === 'risk' || field === 'category') {
                onRiskCategoryChange(rowId, { [field]: nextValue }, "control");
            }
        }

        useImperativeHandle(ref, () => ({}))

        const controlColumns = [
            { key: "risk", label: "Risk Section", type: "text", minWidth: 220 },
            { key: "category", label: "Risk Sub-Section", type: "text", minWidth: 240 },
            { key: "design", label: "Design", type: "number", minWidth: 150 },
            { key: "implementation", label: "Implementation", type: "number", minWidth: 180 },
            { key: "controlEfficacy", label: "Control Efficacy", type: "readonly", minWidth: 180 },
        ]

        const renderCellContent = (column, row) => {
            // Risk and Category are now read-only
            if (column.key === 'risk' || column.key === 'category') {
                return (
                    <div className="flex items-center h-12 px-3 rounded-t-lg bg-navy-100 text-gray-0">
                        {row[column.key] || <span className="text-gray-400">-</span>}
                    </div>
                );
            }

            if (column.key === 'controlEfficacy') {
                return (
                    <div className="flex items-center h-12 px-3 rounded-t-lg bg-navy-100 text-gray-0">
                        {row.controlEfficacy ? (
                            <div className="flex items-center gap-2">
                                <span
                                    className={cn("w-3 h-3 rounded-full", getControlEfficacyColor(row.controlEfficacy))}
                                />
                                <span>{row.controlEfficacy}</span>
                            </div>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            }

            const inputProps = {};
            if (column.key === 'design' || column.key === 'implementation') {
                inputProps.type = "number";
                inputProps.min = 0;
                inputProps.max = CONTROL_EFFICACY_MAX_VALUE;
            }

            return (
                <Input
                    {...inputProps}
                    value={row[column.key]}
                    onChange={(event) =>
                        handleControlRowChange(
                            row.id,
                            column.key,
                            event.target.value
                        )
                    }
                    className="w-full h-12"
                />
            );
        }

        const createHeadClassNames = (stickyKey) => (key) =>
            cn(
                "p-4 text-xs font-semibold tracking-wide uppercase text-gray-200 whitespace-nowrap bg-transparent",
                key === stickyKey && "sticky left-0 z-30 bg-navy-600"
            )

        const createCellClassNames = (stickyKey) => (key) =>
            cn(
                "p-3 text-sm text-gray-0 last:border-b-0",
                key === stickyKey && "sticky left-0 z-10 bg-navy-600"
            )

        const headClassNames = createHeadClassNames(controlColumns[0]?.key)
        const cellClassNames = createCellClassNames(controlColumns[0]?.key)

        const columnStyle = (column) => ({
            minWidth: column.minWidth,
        })

        return (
            <Card className="pt-4 border-none slide-up">
                <CardContent className="p-0">
                    <div className="space-y-4">
                        <div className="relative custom-scrollbar">
                            {controlRows.length === 0 ? (
                                <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-gray-400 border border-gray-600 border-dashed rounded-lg">
                                    <p className="text-base font-semibold text-gray-0">No control efficacy rows yet</p>
                                    <p className="text-sm text-gray-400">First, add a risk and category to the mine type. <a href="/mine-types" className="text-red-500 capitalize">Click here</a> to add a risk and category.</p>
                                </div>
                            ) : (
                                <>
                                    <div className="sticky top-[48px] z-20 bg-navy-600">
                                        <Table className="table-fixed w-full" containerClassName="overflow-visible">
                                            <colgroup>
                                                {controlColumns.map((column) => (
                                                    <col key={column.key} style={{ width: column.minWidth }} />
                                                ))}
                                            </colgroup>
                                            <TableHeader className="text-left">
                                                <TableRow>
                                                    {controlColumns.map((column) => (
                                                        <TableHead
                                                            key={column.key}
                                                            className={headClassNames(column.key)}
                                                            style={columnStyle(column)}
                                                        >
                                                            {column.label}
                                                        </TableHead>
                                                    ))}
                                                </TableRow>
                                            </TableHeader>
                                        </Table>
                                    </div>
                                    <Table className="table-fixed w-full" containerClassName="overflow-visible">
                                        <colgroup>
                                            {controlColumns.map((column) => (
                                                <col key={column.key} style={{ width: column.minWidth }} />
                                            ))}
                                        </colgroup>
                                        <TableBody>
                                            {controlRows.map((row) => {
                                                const rowClassName = removingRowIds.includes(row.id)
                                                    ? "row-exit"
                                                    : enteringRowIds.includes(row.id)
                                                        ? "row-enter"
                                                        : ""
                                                return (
                                                    <React.Fragment key={`control-row-${row.id}`}>
                                                        <TableRow className={rowClassName}>
                                                            {controlColumns.map((column) => (
                                                                <TableCell
                                                                    key={column.key}
                                                                    className={cellClassNames(column.key)}
                                                                    style={columnStyle(column)}
                                                                >
                                                                    {renderCellContent(column, row)}
                                                                </TableCell>
                                                            ))}
                                                        </TableRow>
                                                    </React.Fragment>
                                                )
                                            })}
                                        </TableBody>
                                    </Table>
                                </>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>
        )
    })

ControlEfficacyTab.displayName = "ControlEfficacyTab"

export default ControlEfficacyTab;
export { createEmptyControlRow };

